'use client';

import { useState } from 'react';

import { useEnsureChargesEnabled } from '@ps/features/stripe-connect/context/useEnsureChargesEnabled';
import {
  PopStoreExtendedSubscriptionLevel,
  type Subscriptions,
} from '@ps/models/lib/creator-subscription';
import { ChevronDown, ChevronUp, Lock, Pencil } from 'lucide-react';

import { SubscriptionLevelBadge } from '../../components/SubscriptionLevelBadge';
import { isActiveSubscription } from '../functions/access-control';
import { getSubscriptionDisplayName } from '../functions/subscription-display';
import { useSubscriptionConfigurationForm } from '../hooks/useSubscriptionConfigurationForm';
import type { SubscriptionConfigurationItem } from '../types';
import { SubscriptionSetupField } from './SubscriptionSetupField';

interface Props {
  item: SubscriptionConfigurationItem;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Sibling tiers, threaded through for duplicate-name validation. */
  subscriptions: Subscriptions | undefined;
  sellerId: string | undefined;
  /** Fires after the per-tier save round-trip succeeds. */
  onSaved?: () => void;
  /** Fires when the save / upload throws. */
  onError?: (message: string) => void;
  /** Emphasize the collapsed row's border (mint accent) to mark
   *  it as the configure-flow target tier. */
  highlighted?: boolean;
}

/**
 * Per-tier accordion row inside `ManageSubscriptionModal`. 1:1
 * with legacy `SubscriptionSetupField` (header + body).
 *
 *   - Header: `SubscriptionLevelBadge` (iconOnly) + tier title +
 *     pencil-edit icon (paid + open only) + subtitle ("Active",
 *     "Disabled", "Not configured", "Setup required"). When open
 *     and paid, an "Enable" checkbox sits on the far right.
 *     Locked tiers show a lock icon.
 *   - Body (open only): `SubscriptionSetupField` — price,
 *     optional VIP discount, description, save.
 *
 * Form lives at the row level so both header (Enable + inline
 * title) and body share a single form instance.
 */
export function SubscriptionTierRow({
  item,
  open: openProp,
  onOpenChange,
  subscriptions,
  sellerId,
  onSaved,
  onError,
  highlighted,
}: Props) {
  const { subscription, disabled, minSubscriptionPrice, maxSubscriptionPrice } =
    item;
  const [openState, setOpenState] = useState(item.defaultOpen ?? false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const open = openProp ?? openState;

  const ensureChargesEnabled = useEnsureChargesEnabled();

  const form = useSubscriptionConfigurationForm(subscription, {
    subscriptions,
    sellerId,
    minSubscriptionPrice,
    maxSubscriptionPrice,
  });

  const isFollower =
    subscription.level === PopStoreExtendedSubscriptionLevel.FOLLOWER;

  const savedTitle = isFollower
    ? (item.title ?? 'Followers (Free)')
    : getSubscriptionDisplayName(
        'subscription' in subscription ? subscription.subscription : undefined,
        subscription.level,
      );
  const displayTitle =
    !isFollower && form.value.title ? form.value.title : savedTitle;

  // Subtitle — "Active" / "Disabled" / "Setup required" /
  // "Not configured". Matches legacy `Header` subtitle logic.
  const subtitle = (() => {
    if (isFollower) {
      const followerSub = subscription as Extract<
        typeof subscription,
        { level: PopStoreExtendedSubscriptionLevel.FOLLOWER }
      >;
      if (!followerSub.description) return 'Not configured';
      return followerSub.active === false ? 'Disabled' : 'Active';
    }
    const paidSub = subscription as Extract<
      typeof subscription,
      {
        level: Exclude<
          typeof subscription.level,
          PopStoreExtendedSubscriptionLevel.FOLLOWER
        >;
      }
    >;
    const configured = isActiveSubscription(paidSub.subscription);
    if (!configured) {
      return open ? 'Setup required' : 'Not configured';
    }
    return form.value.active === false ? 'Disabled' : 'Active';
  })();

  const handleToggle = () => {
    if (disabled) return;
    setIsEditingTitle(false);
    const next = !open;
    const applyToggle = () => {
      if (openProp == null) setOpenState(next);
      onOpenChange?.(next);
    };
    // Gate opening a PAID tier on Stripe charges being enabled — a creator
    // can't configure a paid subscription until onboarding is complete.
    // Collapsing and the free FOLLOWER tier are ungated.
    if (next && !isFollower) {
      ensureChargesEnabled('setup subscription', applyToggle);
      return;
    }
    applyToggle();
  };

  const canEditTitle = open && !isFollower;
  const subtitleColor =
    subtitle === 'Active'
      ? 'text-brand-text'
      : subtitle === 'Disabled'
        ? 'text-raise/55'
        : 'text-raise/55';

  return (
    <div
      className={`rounded-xl border transition ${
        open
          ? 'border-brand/50 bg-brand/[0.04]'
          : highlighted
            ? 'border-brand/60 bg-raise/[0.02]'
            : 'border-raise/10 bg-raise/[0.02]'
      } ${disabled ? 'opacity-50' : ''}`}
    >
      {/* Header — single pressable row. Pencil / checkbox /
          input children stop propagation to keep the row from
          collapsing on click. */}
      <div
        role={isEditingTitle || disabled ? undefined : 'button'}
        tabIndex={isEditingTitle || disabled ? -1 : 0}
        onClick={() => {
          if (isEditingTitle || disabled) return;
          handleToggle();
        }}
        onKeyDown={e => {
          if (isEditingTitle || disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
        className={`flex w-full items-center gap-3 px-4 py-3 ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        {/* Tier badge — iconOnly SVG. Returns null for FOLLOWER
            (legacy doesn't ship a follower badge). */}
        <SubscriptionLevelBadge level={subscription.level} iconOnly />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {isEditingTitle && canEditTitle ? (
            <div
              className="flex flex-col gap-1"
              onClick={e => e.stopPropagation()}
            >
              <span className="text-raise/60 text-fine">
                Tier Name <span className="text-danger">*</span>
              </span>
              <input
                type="text"
                autoFocus
                value={form.value.title}
                onChange={e => form.setValue('title', e.target.value)}
                onBlur={() => setIsEditingTitle(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    e.preventDefault();
                    setIsEditingTitle(false);
                  }
                }}
                maxLength={14}
                placeholder="Enter tier name"
                className="focus:border-action border-raise/10 bg-raise/5 text-content-1 placeholder:text-raise/40 h-9 rounded-lg border px-2 text-base focus:outline-none"
              />
              {form.errors.title ? (
                <span className="text-fine text-danger">
                  {form.errors.title}
                </span>
              ) : null}
              <span className={`text-fine ${subtitleColor}`}>{subtitle}</span>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-1.5">
                <span className="text-content-1 text-body truncate">
                  {displayTitle}
                </span>
                {canEditTitle ? (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setIsEditingTitle(true);
                    }}
                    className="text-brand-text flex h-5 w-5 shrink-0 items-center justify-center"
                    aria-label="Edit tier name"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
              <span className={`text-fine ${subtitleColor}`}>{subtitle}</span>
            </>
          )}
        </div>

        {/* Right side — Enable checkbox (open paid) sits next to
            the chevron. Lock replaces both when the tier is
            plan-gated. Chevron is always visible (legacy
            `Collapsible.Panel` keeps the chevron rendered open
            and closed; we just rotate it). */}
        <div className="flex shrink-0 items-center gap-2">
          {open && !isFollower && !disabled ? (
            <label
              onClick={e => e.stopPropagation()}
              className="flex shrink-0 cursor-pointer items-center gap-1.5"
            >
              <input
                type="checkbox"
                checked={form.value.active !== false}
                onChange={e => form.setValue('active', e.target.checked)}
                className="accent-mint-green-300 h-4 w-4 cursor-pointer"
              />
              <span className="text-content-1 text-fine">Enable</span>
            </label>
          ) : null}
          {disabled ? (
            <Lock className="text-raise/40 h-4 w-4 shrink-0" aria-hidden />
          ) : open ? (
            <ChevronUp className="text-raise/60 h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ChevronDown
              className="text-raise/60 h-4 w-4 shrink-0"
              aria-hidden
            />
          )}
        </div>
      </div>

      {/* Body — only on open + not-disabled. */}
      {open && !disabled ? (
        <div className="border-raise/10 border-t px-4 py-4">
          <SubscriptionSetupField
            form={form}
            extendedSubscription={subscription}
            minSubscriptionPrice={minSubscriptionPrice}
            maxSubscriptionPrice={maxSubscriptionPrice}
            onSaved={onSaved}
            onError={onError}
          />
        </div>
      ) : null}
    </div>
  );
}
